/**
 * ContentContext - React context for CMS media overrides
 * 
 * Provides getMedia(key, fallback) helper to components
 * for accessing media URLs from CMS with fallback to defaults.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// Context shape
const ContentContext = createContext({
    media: {},
    contentVersion: 0,
    setMedia: () => { },
    setContentVersion: () => { },
});

/**
 * ContentProvider - Wrap your app with this to enable media overrides
 */
export function ContentProvider({ children }) {
    const [media, setMedia] = useState({});
    const [contentVersion, setContentVersion] = useState(0);

    const value = {
        media,
        contentVersion,
        setMedia,
        setContentVersion,
    };

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
}

/**
 * useContent - Hook for accessing CMS content
 * 
 * @returns {Object} Content helpers
 * @returns {Function} getMedia - Get media value with fallback
 * @returns {Object} media - Raw media object
 * @returns {number} contentVersion - Current content version
 */
export function useContent() {
    const { media, contentVersion } = useContext(ContentContext);

    /**
     * Get a media value from CMS with fallback
     * @param {string} key - Media key (e.g., 'featuredVideo.youtubeUrl')
     * @param {string} fallback - Fallback value if key not in CMS
     * @returns {string} Media value
     */
    const getMedia = useCallback((key, fallback) => {
        const value = media[key];
        return value !== undefined && value !== null && value !== '' ? value : fallback;
    }, [media]);

    return {
        getMedia,
        media,
        contentVersion,
    };
}

/**
 * useContentUpdater - Hook for updating content (used by auto-refresh)
 * 
 * @returns {Object} Update functions
 */
export function useContentUpdater() {
    const { setMedia, setContentVersion } = useContext(ContentContext);

    return {
        setMedia,
        setContentVersion,
    };
}

export { ContentContext };
export default ContentContext;
